import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AsyncMultiSelectDropdown,
  CheckboxInput,
  CheckboxInputProps,
  DateInput,
  Dropdown,
  FormTemplate,
  IfFormData,
  RadioButtonsGroup,
  Row4x4x4x,
  SubmitButton,
} from 'ui-builder';
import {
  AdditionalWorkflowDateOption,
  FileType,
  ProjectType,
  useAdditionalWorkflowAiService,
  usePlansService,
  useProjectPlanService,
} from 'nekst-api';
import {
  FileUploaderInput,
  usePopupFormManager,
} from 'features/nekst-widgets';
import { useProjectContext } from '../../view/ProjectContext';
import { useDataLoaderNew } from 'data-loader';
import { AiAnalysisLoaderWidget } from '../Shared/AiAnalysisLoaderWidget';
import CurrentTransactionDetails from './CurrentTransactionDetails';

type WorkflowMode = 'manual' | 'ai';

function WorkflowMultiDropdown(props: {
  projectType: ProjectType;
  setupAreaId: number;
}) {
  const plansService = usePlansService();

  return (
    <AsyncMultiSelectDropdown
      source="plansIds"
      label="Select Workflow(s)"
      loadOptionsFunc={async () => {
        const plans = await plansService.getAllTeamPlans(props.projectType, props.setupAreaId);
        return plans.map((plan) => ({
          value: plan.id,
          label: plan.name,
        }));
      }}
    />
  );
}

function InputAlignedCheckbox(props: CheckboxInputProps) {
  return (
    <div className="pt-8">
      <div className="flex items-center h-[45px]">
        <CheckboxInput {...props} />
      </div>
    </div>
  );
}

const EXISTING_TASKS_OPTIONS = [
  { value: false, label: 'Keep existing tasks' },
  { value: true, label: 'Delete existing tasks' },
];

function ExistingTasksDropdown() {
  return (
    <Dropdown<boolean>
      source="deleteExistingTasks"
      label="What should happen with existing tasks?"
      options={EXISTING_TASKS_OPTIONS}
    />
  );
}

interface WorkflowSelectionFormData {
  mode: WorkflowMode;
  plansIds: number[];
  fileId?: number;
  useCurrentDates: boolean;
  customStartDate?: Date;
  customEndDate?: Date;
  deleteExistingTasks: boolean;
}

const isAiMode = (data: WorkflowSelectionFormData) => data.mode === 'ai';

export default function WorkflowSelectionModal(props: { projectId: number }) {
  const projectPlanService = useProjectPlanService();
  const additionalWorkflowAiService = useAdditionalWorkflowAiService();
  const popupFormManager = usePopupFormManager();
  const navigate = useNavigate();
  const projectContext = useProjectContext();
  const plansService = usePlansService();

  const projectType = projectContext.data?.type;
  const setupAreaId = projectContext.data?._setupArea?.id;

  const { data: allPlans } = useDataLoaderNew(async () => {
    if (!projectType || !setupAreaId) {
      return [];
    }
    return plansService.getAllTeamPlans(projectType, setupAreaId);
  }, { dependencies: [projectType, setupAreaId] });

  const aiPlans = (allPlans || []).filter((p) => p.isLaunchingFromContractSupported);
  const hasAiPlans = aiPlans.length > 0;

  const submitFunc = async (data: WorkflowSelectionFormData) => {
    const dateOption = data.useCurrentDates
      ? AdditionalWorkflowDateOption.USE_TRANSACTION_DATES
      : AdditionalWorkflowDateOption.UPDATE_TRANSACTION_DATES;

    const createdPlan = await projectPlanService.addNewPlansToProject(
      props.projectId,
      {
        plansIds: data.plansIds,
        dateOption,
        customStartDate: !data.useCurrentDates ? data.customStartDate : undefined,
        customEndDate: !data.useCurrentDates ? data.customEndDate : undefined,
        deleteExistingTasks: data.deleteExistingTasks,
      },
    );

    if (data.mode === 'ai') {
      if (!data.fileId) {
        throw new Error('fileId is required for AI mode');
      }

      await additionalWorkflowAiService.analyzeContract(
        props.projectId,
        createdPlan.id,
        data.fileId,
      );

      popupFormManager.closePopup();
      navigate(`/transactions/${props.projectId}/plans/${createdPlan.id}/ailaunch/verifydata`);
    } else {
      popupFormManager.closePopup();
      const firstStep = createdPlan.hasGroups ? 'groups' : 'keydates';
      navigate(`/transactions/${props.projectId}/plans/${createdPlan.id}/launch/${firstStep}`);
    }
  };

  if (!projectType || !setupAreaId) {
    return null;
  }

  return (
    <FormTemplate<WorkflowSelectionFormData>
      submitFormFunc={submitFunc}
      initialData={{
        mode: 'manual' as WorkflowMode,
        plansIds: [],
        useCurrentDates: true,
        customStartDate: projectContext.data?.startDate,
        customEndDate: projectContext.data?.endDate,
        deleteExistingTasks: false,
      }}
      title="Add Workflow"
      buttons={<SubmitButton text="Continue" />}
    >
      <CurrentTransactionDetails />
      {hasAiPlans && (
        <RadioButtonsGroup<string>
          source="mode"
          label="How would you like to add the workflow?"
          options={[
            { value: 'manual', label: 'Manually' },
            { value: 'ai', label: 'Import from Contract' },
          ]}
        />
      )}
      <WorkflowMultiDropdown
        projectType={projectType}
        setupAreaId={setupAreaId}
      />
      <Row4x4x4x>
        <InputAlignedCheckbox source="useCurrentDates" label="Use Current Dates" />
        <DateInput
          source="customStartDate"
          label="Start Date"
          isDisabled={(data) => !!data.useCurrentDates}
        />
        <DateInput
          source="customEndDate"
          label="End Date"
          isDisabled={(data) => !!data.useCurrentDates}
        />
      </Row4x4x4x>
      <ExistingTasksDropdown />
      <IfFormData<WorkflowSelectionFormData> conditionFunc={isAiMode}>
        <FileUploaderInput
          fileType={FileType.PROJECT_DOCUMENT}
          fileDataSource="_file"
          source="fileId"
          label="Contract Document"
        />
        <AiAnalysisLoaderWidget />
      </IfFormData>
    </FormTemplate>
  );
}
